import type { ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

/**
 * The square that anchors a row: a channel, a product without a photograph.
 * Given an image — a channel's logo — it shows the whole of it, since a mark is
 * never cropped; given neither an image nor content, the first letter of its
 * label. `imageDark` is the same mark drawn for a dark ground, shown instead on
 * the dark theme (ADR 0009 §3); the choice is the theme's CSS variant, so it
 * follows the theme switch without a render.
 */
export function Tile({
    label,
    image,
    imageDark,
    size = 'md',
    children,
}: {
    label?: string;
    image?: string;
    imageDark?: string;
    size?: 'sm' | 'md' | 'lg';
    children?: ReactNode;
}) {
    const initial = (label ?? '').trim().charAt(0).toLocaleUpperCase('tr-TR') || '·';
    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-control border border-border bg-background font-semibold text-muted-foreground',
                size === 'sm' && 'size-8 text-13',
                size === 'md' && 'size-9 text-14',
                size === 'lg' && 'size-11 rounded-card text-18',
            )}
        >
            {image ? (
                <>
                    <img src={image} alt="" className={cn('size-full object-contain p-1', imageDark && 'dark:hidden')} />
                    {imageDark && <img src={imageDark} alt="" className="hidden size-full object-contain p-1 dark:block" />}
                </>
            ) : (
                (children ?? initial)
            )}
        </span>
    );
}
